
import { Component, Prop, Vue, Watch } from 'nuxt-property-decorator';
import sanitizeHtml from 'sanitize-html';
import DynamicElement from '../DynamicElement';

type PositionedHtml = Partial<Record<InsertPosition, string | null>>;

type PositionedHtmlProp = {
  before?: string | null;
  after?: string | null;
};

@Component({
  components: { DynamicElement },
})
export default class RawHtml extends Vue {
  @Prop({ type: String, default: 'div' })
  readonly tag!: string;

  @Prop({ type: [String, Object], default: () => ({}) })
  readonly html!: string | PositionedHtmlProp;

  @Prop({ type: Boolean, default: false })
  readonly preserveBreaks!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly alignLeft!: boolean;

  get htmlContent(): PositionedHtml {
    const { before = null, after = null } = typeof this.html === 'string' ? { after: this.html } : this.html;
    return { afterbegin: before, beforeend: after };
  }

  @Watch('htmlContent', { immediate: true })
  async renderHTML() {
    await this.$nextTick();
    if (!this.$el) return;

    // sanitizer configuration
    const allowedAttributes = sanitizeHtml.defaults.allowedAttributes;
    allowedAttributes.iframe = ['src', 'width', 'height', 'frameborder', 'allowfullscreen', 'allow'];
    allowedAttributes['*'] = ['data-test-id', 'style'];
    allowedAttributes.div = ['data-youtube-video', 'data-vimeo-video', 'data-legacy-video'];

    const allowedIframeDomains = ['youtube.com', 'youtu.be', 'vimeo.com', 'player.vimeo.com'];

    const allowedTags = sanitizeHtml.defaults.allowedTags;
    allowedTags.push('iframe');

    const allowedClasses = {
      span: ['nowrap'], // receipt page
    };

    const allowedStyles = {
      '*': { 'text-align': [/^center$/, /^left$/] }, // page-builder
    };

    this.$el.innerHTML = '';

    const positions = Object.keys(this.htmlContent) as InsertPosition[];
    positions.forEach(position => {
      const content = this.htmlContent[position];
      if (!content) return;

      const sanitizedContent = sanitizeHtml(content, {
        allowedAttributes,
        allowedClasses,
        allowedStyles,
        allowedIframeDomains,
      });
      this.$el.insertAdjacentHTML(position, sanitizedContent);

      const links: HTMLAnchorElement[] = Array.from(this.$el.querySelectorAll('a[href^="http"]'));
      links.forEach(externalLink => {
        externalLink.target = '_blank';
        externalLink.rel = 'noopener';
      });
    });
  }
}
